import React from 'react';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const getUserBadge = (u) => {
  const className = u.banned ? 'bg-red-100 text-red-700' :
    u.suspended ? 'bg-amber-100 text-amber-700' :
    u.role === 'admin' ? 'bg-brand-accent/20 text-brand-accent' :
    u.role === 'moderator' ? 'bg-purple-100 text-purple-700' :
    'bg-gray-100 text-gray-700';
  const label = u.banned ? 'Banned' : u.suspended ? 'Suspended' : u.role;
  return { className, label };
};

const UserActions = ({ u, user, api, fetchData, handleRoleChange }) => {
  const isFounder = ['hbwickhams@gmail.com', 'japone729@gmail.com'].includes(u.email?.toLowerCase());
  if (u.id === user.id) return null;

  const resetPassword = async () => {
    const newPwd = window.prompt(`Set a new password for ${u.display_name || u.username}:`);
    if (newPwd && newPwd.length >= 6) {
      try {
        await api.post('/admin/reset-user-password', { user_id: u.id, new_password: newPwd });
        alert(`Password reset for ${u.display_name || u.username}`);
      } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    } else if (newPwd) {
      alert('Password must be at least 6 characters');
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {isFounder && <span className="text-xs text-text-muted italic" data-testid={`founder-badge-${u.id}`}>Founder</span>}
      <Button variant="outline" size="sm" onClick={resetPassword}
        className="text-brand-accent border-brand-accent/30 hover:bg-brand-accent/10" data-testid={`reset-pw-${u.id}`}>
        Reset Password
      </Button>
      {!isFounder && (
        <>
          <Select value={u.role} onValueChange={(value) => handleRoleChange(u.id, value)}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="writer">Writer</SelectItem>
              <SelectItem value="moderator">Moderator</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" data-testid={`warn-user-${u.id}`}
            className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
            onClick={async () => {
              const msg = window.prompt('Warning message:', 'Please review our community guidelines.');
              if (msg) { try { await api.post(`/admin/warn-user?user_id=${u.id}&message=${encodeURIComponent(msg)}`); fetchData(); alert('Warning sent'); } catch (e) { alert(e.response?.data?.detail || 'Error'); } }
            }}>Warn</Button>
          {u.suspended ? (
            <Button variant="outline" size="sm" data-testid={`unsuspend-user-${u.id}`}
              className="text-green-600 border-green-300 hover:bg-green-50"
              onClick={async () => { if (window.confirm(`Lift suspension for ${u.display_name || u.username}?`)) { try { await api.post(`/admin/unsuspend-user?user_id=${u.id}`); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Error'); } } }}>
              Unsuspend
            </Button>
          ) : !u.banned ? (
            <Button variant="outline" size="sm" data-testid={`suspend-user-${u.id}`}
              className="text-amber-600 border-amber-300 hover:bg-amber-50"
              onClick={async () => { if (window.confirm(`Suspend ${u.display_name || u.username}?`)) { try { await api.post(`/admin/suspend-user?user_id=${u.id}`); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Error'); } } }}>
              Suspend
            </Button>
          ) : null}
          {u.banned ? (
            <Button variant="outline" size="sm" data-testid={`unban-user-${u.id}`}
              className="text-green-600 border-green-300 hover:bg-green-50"
              onClick={async () => { if (window.confirm(`Unban ${u.display_name || u.username}?`)) { try { await api.post(`/admin/unban-user?user_id=${u.id}`); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Error'); } } }}>
              Unban
            </Button>
          ) : !u.suspended ? (
            <Button variant="outline" size="sm" data-testid={`ban-user-${u.id}`}
              className="text-red-600 border-red-300 hover:bg-red-50"
              onClick={async () => { if (window.confirm(`Ban ${u.display_name || u.username}?`)) { try { await api.post(`/admin/ban-user?user_id=${u.id}`); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Error'); } } }}>
              Ban
            </Button>
          ) : null}
          <Button variant="outline" size="sm" data-testid={`delete-user-${u.id}`}
            className="text-red-600 border-red-300 hover:bg-red-50"
            onClick={async () => { if (window.confirm(`PERMANENTLY DELETE ${u.display_name || u.username} and ALL their content?`)) { try { await api.delete(`/admin/delete-user?user_id=${u.id}`); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Error'); } } }}>
            Delete
          </Button>
        </>
      )}
    </div>
  );
};

export const AdminUsersTab = ({ users, user, api, fetchData, handleRoleChange }) => (
  <Card className="border-brand-primary/10">
    <CardHeader><CardTitle>User Management</CardTitle></CardHeader>
    <CardContent>
      <div className="space-y-4">
        {users.map((u) => {
          const badge = getUserBadge(u);
          return (
            <div key={u.id} className="flex items-center justify-between p-4 bg-surface-alt rounded-lg" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-brand-primary flex items-center justify-center text-white font-medium">
                  {(u.display_name || u.username)?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-text-primary">{u.display_name || u.username}</p>
                  <p className="text-sm text-text-muted">@{u.username} &bull; {u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={badge.className}>{badge.label}</Badge>
                {u.warning_count > 0 && <span className="text-xs text-amber-600">{u.warning_count} warning{u.warning_count > 1 ? 's' : ''}</span>}
                <UserActions u={u} user={user} api={api} fetchData={fetchData} handleRoleChange={handleRoleChange} />
              </div>
            </div>
          );
        })}
      </div>
    </CardContent>
  </Card>
);
